/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type {
  EntityRoleType,
  EntityType,
  EntityVersionType,
  FieldType,
} from "@/schemas/digital-files";
import { createField, getSectionsFromEntity } from "../../utils";
import type { EntityField, Section } from "../../types";

interface EntityContextType {
  entityTitle: string;
  entityVersion: number;
  entityVersions: EntityVersionType[];
  setEntityTitle: (title: string) => void;
  sections: Section[];
  addSection: () => void;
  updateSectionTitle: (sectionId: string, title: string) => void;
  updateSectionDescription: (sectionId: string, description: string) => void;
  removeSection: (sectionId: string) => void;
  reorderSections: (newSections: Section[]) => void;
  addField: (sectionId: string, config: EntityField) => void;
  updateField: (
    sectionId: string,
    fieldId: string,
    updatedField: EntityField,
  ) => void;
  removeField: (sectionId: string, fieldId: string) => void;
  selectFieldForSummary: (sectionId: string, fieldId: string) => void;
  unselectFieldFromSummary: (sectionId: string, fieldId: string) => void;
  reset: () => void;
  isValid: boolean;
  fieldErrors: Record<string, string>;
  id: string;
  rolesList: EntityRoleType[];
  addRole: (name: string) => void;
  removeRole: (name: string) => void;
  isCreateMode: boolean;
}

const EntityContext = createContext<EntityContextType | undefined>(undefined);

export const useEntity = () => {
  const context = useContext(EntityContext);
  if (!context) {
    throw new Error("useEntity must be used within an EntityProvider");
  }
  return context;
};

interface EntityProviderProps {
  children: ReactNode;
  initialData?: EntityType;
  isNewEntity: boolean;
}

export const EntityProvider = ({
  children,
  initialData,
  isNewEntity,
}: EntityProviderProps) => {
  const [entityTitle, setEntityTitle] = useState("");
  const [sections, setSections] = useState<Section[]>([]);
  const [rolesList, setRolesList] = useState<EntityRoleType[]>([]);

  useEffect(() => {
    if (initialData) {
      setEntityTitle(initialData.name ?? "");
      setSections(getSectionsFromEntity(initialData));
      setRolesList(initialData.roles ?? []);
    } else {
      setSections([
        {
          id: crypto.randomUUID(),
          title: "",
          order: 1,
          fields: [],
        },
      ]);
    }
  }, [initialData, setSections]);

  const addSection = () => {
    const newSection: Section = {
      id: crypto.randomUUID(),
      title: "",
      order: sections.length + 1,
      fields: [],
    };
    setSections([...sections, newSection]);
  };

  const updateSectionTitle = (sectionId: string, title: string) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId ? { ...section, title } : section,
      ),
    );
  };

  const updateSectionDescription = (sectionId: string, description: string) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId ? { ...section, description } : section,
      ),
    );
  };

  const removeSection = (sectionId: string) => {
    if (sections.length <= 1) {
      return;
    }
    setSections(sections.filter((section) => section.id !== sectionId));
  };

  const reorderSections = (newSections: Section[]) => {
    setSections(newSections);
  };

  const addField = (sectionId: string, config: EntityField) => {
    const selectedSection = sections.find((sec) => sec.id === sectionId);
    const newField: FieldType = createField(config, selectedSection);
    setSections(
      sections.map((section) =>
        section.id === sectionId
          ? { ...section, fields: [...section.fields, newField] }
          : section,
      ),
    );
  };

  const removeField = (sectionId: string, fieldId: string) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: section.fields.filter((field) => field.id !== fieldId),
            }
          : section,
      ),
    );
  };

  const updateField = (
    sectionId: string,
    fieldId: string,
    updatedField: EntityField,
  ) => {
    const section = sections.find((sec) => sec.id === sectionId);
    const newField: FieldType = createField(updatedField, section);
    setSections(
      sections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: section.fields.map((field) =>
                field.id === fieldId ? newField : field,
              ),
            }
          : section,
      ),
    );
  };

  const selectFieldForSummary = (sectionId: string, fieldId: string) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: section.fields.map((field) =>
                field.id === fieldId
                  ? {
                      ...field,
                      configuration: field.configuration
                        ? {
                            ...field.configuration,
                            options: field.configuration.options || [],
                            show_in_summary: true,
                          }
                        : null,
                    }
                  : field,
              ),
            }
          : section,
      ),
    );
  };
  const unselectFieldFromSummary = (sectionId: string, fieldId: string) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: section.fields.map((field) =>
                field.id === fieldId
                  ? {
                      ...field,
                      configuration: field.configuration
                        ? {
                            ...field.configuration,
                            options: field.configuration.options || [],
                            show_in_summary: false,
                          }
                        : null,
                    }
                  : field,
              ),
            }
          : section,
      ),
    );
  };

  const reset = () => {
    setEntityTitle("");
    setSections([]);
  };

  const addRole = (name: string) => {
    setRolesList([
      ...rolesList,
      {
        id: `role_${crypto.randomUUID()}_${rolesList.length + 1}`,
        name,
        order: rolesList.length + 1,
      },
    ]);
  };

  const removeRole = (id: string) => {
    setRolesList(rolesList.filter((role) => role.id !== id));
  };

  // Create a record of field errors for duplicate names
  const fieldErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    const fieldNameMap = new Map<string, string[]>();

    // Group field IDs by their names (lowercase)
    sections.forEach((section) => {
      section.fields.forEach((field) => {
        const normalizedName = field.name.trim().toLowerCase();
        if (!fieldNameMap.has(normalizedName)) {
          fieldNameMap.set(normalizedName, []);
        }
        fieldNameMap.get(normalizedName)!.push(field.id);
      });
    });

    // Mark all fields with duplicate names as errors
    fieldNameMap.forEach((fieldIds) => {
      if (fieldIds.length > 1) {
        fieldIds.forEach((id) => {
          errors[id] = `Este nombre del campo está duplicado`;
        });
      }
    });

    return errors;
  }, [sections]);

  const isValid = useMemo(
    () =>
      entityTitle.trim() !== "" &&
      sections.length >= 1 &&
      sections.every((section) => section.fields.length > 0) &&
      sections.every((section) => section.title.trim() !== "") &&
      Object.keys(fieldErrors).length === 0,
    [entityTitle, sections, fieldErrors],
  );

  const entityVersions = useMemo(() => {
    return initialData?.versions ?? [];
  }, [initialData]);

  const entityVersion = useMemo(() => {
    return initialData?.version ?? 0;
  }, [initialData]);

  const value: EntityContextType = {
    id: initialData?.id || "",
    entityTitle,
    entityVersion,
    entityVersions,
    setEntityTitle,
    sections,
    addSection,
    updateSectionTitle,
    updateSectionDescription,
    removeSection,
    reorderSections,
    addField,
    updateField,
    removeField,
    isValid,
    fieldErrors,
    selectFieldForSummary,
    unselectFieldFromSummary,
    reset,
    rolesList,
    addRole,
    removeRole,
    isCreateMode: isNewEntity,
  };

  return (
    <EntityContext.Provider value={value}>{children}</EntityContext.Provider>
  );
};
